import { ReactComponent as ConductInspectionIcon } from 'assets/images/conduct.svg';
import { ReactComponent as LogIcon } from 'assets/images/log.svg';
import { ReactComponent as RecordIncidentIcon } from 'assets/images/record-incident.svg';
import { ReactComponent as ScheduleInspectionIcon } from 'assets/images/schedule-inspection.svg';
import { profileDropdown, userRoles } from 'constants/constants';
import { Col, Container, Dropdown, Row, Stack } from 'react-bootstrap';
import { SelectLanguage } from './common/SelectLanguage';
import {
  Avatar,
  Badge,
  Divider,
  Hidden,
  IconButton,
  List,
  ListItem,
  Popover,
  Typography,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { RootStates } from 'redux/store';
import { SelectInspectionTypeModal } from './common/SelectInspectionTypeModal';
import { useCallback, useEffect, useState } from 'react';
import { onLogout } from '../redux/authSlice';
import { useTranslation } from 'react-i18next';
import { ReferInspectSmart } from './common/ReferInspectSmart';
import {
  Business,
  Call,
  Email,
  GradingOutlined,
  ListOutlined,
  NotificationsActive,
  NotificationsOutlined,
} from '@mui/icons-material';
import { Icon } from './common/Icon';
import './Header.scss';
import { routesPaths } from 'constants/paths';
import {
  setHeaderTitle,
  setUnreadNotifications,
  showDropdown as showHeaderDropdown,
} from 'redux/projectSlice';
import { Notifications } from './Notifications';
import {
  useGetUnreadNotificationCountMutation,
  useMarkAllNotificationAsReadMutation,
} from 'redux/services/project';

interface HeaderProps {
  sidebarHandler: () => void;
}

const Header = ({ sidebarHandler }: HeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const [getUnreadNotifications, { data }] =
    useGetUnreadNotificationCountMutation();
  const [markAllAsRead] = useMarkAllNotificationAsReadMutation();
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showNotificationDrawer, setShowNotificationDrawer] = useState(false);
  const [showReferDialog, setShowReferDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [displayMenu, setDisplayMenu] = useState(false);
  const navigate = useNavigate();
  const { userDetails }: any = useSelector((state: RootStates) => state.auth);
  const unreadNotifications = useSelector(
    (state: RootStates) => state.project.unreadNotifications,
  );
  const { showDropdown, role } = useSelector(
    (state: RootStates) => state.project,
  );
  const headerTitle = useSelector(
    (state: RootStates) => state.project.headerTitle,
  );
  const { projectId, inspectionId, observationId, incidentId, step, libId } =
    useParams();

  const dropdownOptions = [
    {
      name: t('conduct_inspection'),
      icon: <ConductInspectionIcon />,
      url: `/project/${projectId}/conduct-inspection`,
      link: false,
    },
    {
      name: t('header_log_observation'),
      icon: <LogIcon />,
      url: `/project/${projectId}/log-observation`,
      link: true,
    },
    {
      name: t('header_record_incident'),
      icon: <RecordIncidentIcon />,
      url: `/project/${projectId}/record-incident`,
      link: true,
    },
  ];

  const fetchNotifications = useCallback(async () => {
    getUnreadNotifications({});
  }, [getUnreadNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [location, fetchNotifications]);

  useEffect(() => {
    if (data) {
      dispatch(setUnreadNotifications(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (!projectId) {
      dispatch(showHeaderDropdown(false));
      dispatch(setHeaderTitle(null));
    }
  }, [projectId, dispatch]);

  const getItemClickHandler = (item: any) => () => {
    if (item.name === t('conduct_inspection')) {
      setShowInspectionDialog(true);
    }
  };

  const handleCloseLanguageSelector = () => setShowLanguageSelector(false);
  const handleCloseReferDialog = () => setShowReferDialog(false);
  const handleCloseDialog = () => setShowInspectionDialog(false);
  const getMenuItemClickHandler = (item: { name: string }) => () => {
    switch (item.name) {
      case 'menu_logout': {
        dispatch(onLogout());
        break;
      }
      case 'menu_help': {
        navigate('/help');
        break;
      }
      case 'menu_language': {
        setShowLanguageSelector(true);
        break;
      }
      case 'menu_refer': {
        setShowReferDialog(true);
        break;
      }
      case 'menu_plan_and_subscription': {
        navigate(routesPaths.plans);
        break;
      }
      case 'menu_profile': {
        navigate(routesPaths.profile);
        break;
      }
      default:
        break;
    }
    setAnchorEl(null);
    setDisplayMenu(false);
  };

  const handleTaskClick = () => {
    if (location.pathname.includes('tasks')) {
      return;
    }
    if (projectId && location.pathname.includes('inspection')) {
      navigate(`/project/${projectId}/inspection/tasks`);
    } else if (projectId && location.pathname.includes('observation')) {
      navigate(`/project/${projectId}/observation/tasks`);
    } else if (projectId && location.pathname.includes('incident')) {
      navigate(`/project/${projectId}/incident/tasks`);
    } else if (projectId) {
      navigate(`/project/${projectId}/tasks`);
    } else {
      navigate(routesPaths.allTasks);
    }
  };

  const handleDisplayNotification = async () => {
    await markAllAsRead({});
    dispatch(setUnreadNotifications(0));
    setShowNotificationDrawer(true);
  };

  const handleCloseNotificationDialog = async () => {
    setShowNotificationDrawer(false);
    fetchNotifications();
  };

  const renderNotificationIcon = useCallback(() => {
    return unreadNotifications > 0 ? (
      <Badge badgeContent={unreadNotifications} color="error">
        <NotificationsActive className="notification-active-icon" />
      </Badge>
    ) : (
      <Tooltip title="Notifications" arrow>
        <NotificationsOutlined />
      </Tooltip>
    );
  }, [unreadNotifications]);

  return (
    <div className="d-flex flex-column bg-white">
      <header className="py-2 px-3 d-flex justify-content-between align-items-center main-header">
        <Stack direction="horizontal" gap={3}>
          <IconButton size="large" color="primary" onClick={sidebarHandler}>
            <Hidden mdUp>
              <Icon name="favicon" />
            </Hidden>
            <Hidden mdDown>
              <ListOutlined />
            </Hidden>
          </IconButton>
          {showDropdown && (
            <Dropdown>
              <Dropdown.Toggle className="toggle-button" variant="primary">
                <span>{t('btn_add')}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu
                className={`actions-dropdown py-2 ${
                  role === userRoles.DOER ? 'doer' : ''
                }`}
              >
                <Container fluid>
                  <Row className="actions-content">
                    {role !== userRoles.DOER ? (
                      <>
                        {dropdownOptions.map((item: any) => (
                          <Col
                            sm={4}
                            xs={6}
                            key={item.name}
                            className="d-flex align-items-center"
                          >
                            <Dropdown.Item className="p-0 dropdown-it">
                              {item.link ? (
                                <Link
                                  to={item.url}
                                  className="d-block text-decoration-none py-2"
                                >
                                  <div className="text-center item-container">
                                    <span className="d-block">{item.icon}</span>
                                    <p className="mb-0 mt-2">{item.name}</p>
                                  </div>
                                </Link>
                              ) : (
                                <div
                                  className="text-center item-container py-2"
                                  onClick={getItemClickHandler(item)}
                                >
                                  <span className="d-block">{item.icon}</span>
                                  <p className="mb-0 mt-2">{item.name}</p>
                                </div>
                              )}
                            </Dropdown.Item>
                          </Col>
                        ))}
                      </>
                    ) : (
                      <Col md={4} className="d-flex align-items-center">
                        <Dropdown.Item className="p-0 dropdown-it">
                          <Link
                            to={`/project/${projectId}/schedule-inspection`}
                            className="d-block text-decoration-none py-2"
                          >
                            <div className="text-center item-container">
                              <span className="d-block">
                                <ScheduleInspectionIcon />
                              </span>
                              <p className="mb-0 mt-2">
                                {t('header_schedule_inspection')}
                              </p>
                            </div>
                          </Link>
                        </Dropdown.Item>
                      </Col>
                    )}
                  </Row>
                </Container>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Stack>
        {headerTitle && (
          <Stack direction="horizontal">
            <Hidden mdDown>
              <div className="app-header-title d-flex flex-column">
                <span className="h4">{headerTitle.title}</span>
                {headerTitle.subTitle && (
                  <span className="h5 text-center mb-0">
                    {headerTitle.subTitle}
                  </span>
                )}
              </div>
            </Hidden>
          </Stack>
        )}

        <Stack direction="horizontal" gap={2}>
          <IconButton
            color={location.pathname.includes('tasks') ? 'success' : 'primary'}
            disabled={
              !!inspectionId ||
              !!observationId ||
              !!incidentId ||
              !!step ||
              !!libId
            }
            onClick={handleTaskClick}
          >
            <Tooltip title="Tasks" arrow>
              <GradingOutlined color="inherit" />
            </Tooltip>
          </IconButton>
          <IconButton onClick={handleDisplayNotification}>
            {renderNotificationIcon()}
          </IconButton>
          <Avatar
            sx={{ color: '#fff', background: '#4b2988', cursor: 'pointer' }}
            alt={userDetails.name}
            src={userDetails.imageUrl}
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setDisplayMenu(true);
            }}
          >
            {`${userDetails.name?.split(' ')[0]?.[0] ?? ''}${
              userDetails.name?.split(' ')[1]?.[0] ?? ''
            }`}
          </Avatar>
        </Stack>
        <SelectInspectionTypeModal
          open={showInspectionDialog}
          projectId={projectId ?? ''}
          onClose={handleCloseDialog}
        />
        <Popover
          className="profile-popup"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={displayMenu}
          onClose={() => {
            setDisplayMenu(false);
            setAnchorEl(null);
          }}
        >
          <div className="d-flex flex-column" style={{ minWidth: 300 }}>
            <div className="d-flex p-3 w-100 flex-column">
              <div className="d-flex align-items-center flex-column">
                <Avatar
                  sx={{
                    color: '#fff',
                    background: '#4b2988',
                    cursor: 'pointer',
                    width: 78,
                    height: 78,
                    border: '1px solid #4b2988',
                    fontSize: '2.5rem',
                  }}
                  alt={userDetails.name}
                  src={userDetails.imageUrl}
                >
                  {`${userDetails.name?.split(' ')[0]?.[0] ?? ''}${
                    userDetails.name?.split(' ')[1]?.[0] ?? ''
                  }`}
                </Avatar>
                <div className="d-flex flex-column flex-grow-1 align-items-start p-1">
                  <Typography
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      maxWidth: 180,
                    }}
                    color="primary"
                    className="mx-1"
                    variant="h5"
                  >
                    {userDetails.name}
                  </Typography>
                </div>
              </div>

              <div className="d-flex justify-content-between w-100 my-1">
                <Email className="mx-1" />
                <Typography color="primary">{userDetails.email}</Typography>
              </div>
              <div className="d-flex justify-content-between w-100 my-1">
                <Call className="mx-1" />
                <Typography color="primary">{userDetails.mobile}</Typography>
              </div>
              <div className="d-flex justify-content-between w-100">
                <Business className="mx-1" />
                <Typography color="primary">
                  {userDetails.onboardingData?.organization}
                </Typography>
              </div>
            </div>
            <Divider sx={{ background: '#4b2988' }} className="mx-3" />
            <List className="px-2">
              {profileDropdown.map((item: any) => (
                <ListItem
                  key={item.name}
                  onClick={getMenuItemClickHandler(item)}
                >
                  {t(item.name)}
                </ListItem>
              ))}
            </List>
          </div>
        </Popover>
        <SelectLanguage
          open={showLanguageSelector}
          onClose={handleCloseLanguageSelector}
        />
        <ReferInspectSmart
          open={showReferDialog}
          onClose={handleCloseReferDialog}
        />
        {showNotificationDrawer && (
          <Notifications
            open={showNotificationDrawer}
            onClose={handleCloseNotificationDialog}
          />
        )}
      </header>
      {headerTitle && (
        <Hidden mdUp>
          <div className="app-header-title-mobile d-flex flex-column justify-content-center align-items-center">
            <span className="h5">{headerTitle.title}</span>
            {headerTitle.subTitle && (
              <span className="h6 text-center mb-0">
                {headerTitle.subTitle}
              </span>
            )}
          </div>
        </Hidden>
      )}
    </div>
  );
};

export default Header;
